import { z } from 'zod';

/**
 * User-facing auth request schemas.
 *
 * Used by both backend (Fastify route validation) and frontend (form-level
 * client validation via react-hook-form). Keeping one source of truth
 * eliminates the risk of server and client disagreeing about what counts
 * as a valid username.
 */

export const LoginSchema = z.object({
  username: z
    .string()
    .min(3, 'username too short')
    .max(32, 'username too long')
    .regex(/^[a-zA-Z0-9_.@-]+$/, 'username contains invalid characters'),
  password: z.string().min(6, 'password too short').max(128, 'password too long'),
  /** Optional TOTP code when 2FA is enabled on the account. */
  otp: z
    .string()
    .regex(/^\d{6}$/, 'OTP must be 6 digits')
    .optional(),
  /** Cloudflare Turnstile or similar — optional in dev. */
  captcha: z.string().optional(),
});
export type LoginRequest = z.infer<typeof LoginSchema>;

export const RegisterSchema = z
  .object({
    username: z
      .string()
      .min(3)
      .max(32)
      .regex(/^[a-zA-Z0-9_.]+$/),
    email: z.string().email().max(128).optional(),
    phone: z.string().min(7).max(20).optional(),
    password: z
      .string()
      .min(8)
      .max(128)
      .regex(/[A-Z]/, 'must contain an uppercase letter')
      .regex(/[a-z]/, 'must contain a lowercase letter')
      .regex(/[0-9]/, 'must contain a digit'),
    password_confirm: z.string(),
    /** Referrer invite code or username (旗下推荐人). */
    invite_code: z
      .string()
      .min(3)
      .max(32)
      .regex(/^[a-zA-Z0-9_.]+$/, 'sponsor must be a valid username or invite code'),
    /** Which leg of the binary tree the referrer chose for this new user. */
    position: z.enum(['L', 'R']),
    captcha: z.string().optional(),
  })
  .refine((v) => v.password === v.password_confirm, {
    path: ['password_confirm'],
    message: 'passwords do not match',
  });
export type RegisterRequest = z.infer<typeof RegisterSchema>;

export const RefreshTokenSchema = z.object({
  refresh_token: z.string().min(10),
});
export type RefreshTokenRequest = z.infer<typeof RefreshTokenSchema>;

const ethAddress = z
  .string()
  .regex(/^0x[a-fA-F0-9]{40}$/, 'invalid wallet address');

export const WalletChallengeSchema = z.object({
  address: ethAddress,
});
export type WalletChallengeRequest = z.infer<typeof WalletChallengeSchema>;

export const WalletLoginSchema = z.object({
  address: ethAddress,
  message: z.string().min(20).max(2000),
  signature: z.string().regex(/^0x[a-fA-F0-9]+$/, 'invalid signature'),
});
export type WalletLoginRequest = z.infer<typeof WalletLoginSchema>;

export const WalletChallengeResponseSchema = z.object({
  message: z.string(),
  nonce: z.string().uuid(),
  expires_in: z.number().int().positive(),
});
export type WalletChallengeResponse = z.infer<typeof WalletChallengeResponseSchema>;

export const ChangePasswordSchema = z
  .object({
    old_password: z.string().min(6),
    new_password: z
      .string()
      .min(8)
      .max(128)
      .regex(/[A-Z]/)
      .regex(/[a-z]/)
      .regex(/[0-9]/),
    new_password_confirm: z.string(),
  })
  .refine((v) => v.new_password === v.new_password_confirm, {
    path: ['new_password_confirm'],
    message: 'passwords do not match',
  });
export type ChangePasswordRequest = z.infer<typeof ChangePasswordSchema>;

/**
 * Response envelope for `POST /api/auth/login` and `/api/auth/register`.
 * `access_token` is short-lived (15m); `refresh_token` is long-lived (30d)
 * and is stored in an HttpOnly cookie in the browser.
 */
export const AuthTokenPairSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  /** Seconds until access_token expires. */
  expires_in: z.number().int().positive(),
  token_type: z.literal('Bearer'),
});
export type AuthTokenPair = z.infer<typeof AuthTokenPairSchema>;
